import CustomDateTimePicker from 'Components/CustomDateTimePicker'
import CustomLoadingButton from 'Components/CustomLoadingButton'
import FormTextField from 'Components/FormTextField'
import InfoTooltip from 'Components/InfoTooltip'
import PTAFormField from 'Components/PTAFormField'
import StyledForm from 'Components/StyledForm/StyledForm'
import StyledLabel from 'Components/StyledLabel'
import { WithLoader } from 'Components/WithLoader'
import AllowedApiClients from 'Services/common/Features/AllowedApiClient/Components/AllowedApiClients'
import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { SELECTED_SALES_POINT_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/SalesPoint/Hooks/useSalesPointsWithParams'
import {
  useCreateSingleSalesPointMutation,
  useUpdateSingleSalesPointMutation
} from 'Services/ServiceProviderManagement/Features/SalesPoint/salesPointApi'
import {
  type SalesPoint,
  type SalesPointAddress,
  SalesPointUsage
} from 'Services/ServiceProviderManagement/Features/SalesPoint/types'
import { SELECTED_SERVICE_PROVIDER_PARAMETER_KEY } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Hooks/useServiceProvidersWithParams'
import { SERVICE_PROVIDER_DETAILS_DRAWER_TAB_PARAM_KEY } from 'Services/ServiceProviderManagement/Features/ServiceProvider/Pages/ServiceProviderListPage'
import { useGetAllServiceProvidersQuery } from 'Services/ServiceProviderManagement/Features/ServiceProvider/serviceProviderApi'
import { ServiceProviderTabType } from 'Services/ServiceProviderManagement/Features/ServiceProvider/types'
import type { FieldValidationErrors } from 'Services/ServiceProviderManagement/types'
import { useTypedSelector } from 'Store'
import type { ServiceProviderManagementError } from 'Util/ApiClient'
import { CopyObjectKeys, useCopyObjectContext } from 'Util/Context/copyObjectContext'
import {
  ALLOWED_API_CLIENTS_CONTAINER_MAX_WIDTH,
  FORM_FIELD_DEFAULT_MIN_WIDTH,
  IS_HSL_SERVICE_ENVIRONMENT,
  IS_WALTTI_SERVICE_ENVIRONMENT,
  PTA_PARAM_KEY,
  SERVICE_PROVIDER_MANAGEMENT_ROOT_URL,
  Service,
  SORT_ORDER_ASC
} from 'Util/constants'
import { formatToUTCISO } from 'Util/formatters'
import { useValidationErrors } from 'Util/hooks/useValidationErrors'
import SnackbarUtils from 'Util/Snackbar'
import { appendQueryParamsToUrl } from 'Util/url'
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { t } from 'ttag'

const fieldErrorsDefaultState: FieldValidationErrors = {
  service_provider_id: [],
  sales_point_id: [],
  usage: [],
  start_date: [],
  external_identifier: [],
  name: [],
  customer_reference_name: [],
  s365t_account_id: [],
  business_id: [],
  additional_info: [],
  sales_allowed: [],
  hsl_sales_point: [],
  billed_at_sales_point_level: [],
  transfer_sales_to_financial_system: [],
  phone: [],
  street_address: [],
  postal_code: [],
  city: [],
  source: []
}

interface SalesPointFormProps {
  salesPoint?: SalesPoint
  successBaseUrl: string
}

function SalesPointForm({ salesPoint, successBaseUrl }: Readonly<SalesPointFormProps>) {
  const navigate = useNavigate()
  const isEdit = !!salesPoint
  const { user } = useTypedSelector(state => state.user)

  const [searchParams] = useSearchParams()
  const selectedServiceProviderId = searchParams.get(SELECTED_SERVICE_PROVIDER_PARAMETER_KEY)

  const { fieldErrors, setValidationErrors, getFieldErrorHelperText, getErrorProps } =
    useValidationErrors(fieldErrorsDefaultState)

  const { getCopiedObject, clearCopiedObject } = useCopyObjectContext()
  const copiedSalesPointData = isEdit
    ? null
    : getCopiedObject<SalesPoint>(CopyObjectKeys.SALES_POINT)

  const { selectedAuthorityCode } = useAuthorityContext()

  const salesPointDefaultValues: Partial<SalesPoint> = {
    pta: IS_WALTTI_SERVICE_ENVIRONMENT ? selectedAuthorityCode : undefined,
    service_provider_id: selectedServiceProviderId ?? undefined,
    sales_allowed: true,
    active: true,
    ...(IS_HSL_SERVICE_ENVIRONMENT
      ? {
          hsl_sales_point: false,
          billed_at_sales_point_level: false,
          transfer_sales_to_financial_system: false
        }
      : {})
  }

  const [salesPointDetails, setSalesPointDetails] = useState<Partial<SalesPoint>>(
    (isEdit && salesPoint) || copiedSalesPointData || { ...salesPointDefaultValues }
  )

  // biome-ignore lint/correctness/useExhaustiveDependencies: suppress clearCopiedObject, suppress copiedSalesPointData
  useEffect(() => {
    if (copiedSalesPointData) {
      clearCopiedObject(CopyObjectKeys.SALES_POINT)
    }
  }, [])

  const [createSingleSalesPoint, createStatus] = useCreateSingleSalesPointMutation()
  const [updateSingleSalesPoint, updateStatus] = useUpdateSingleSalesPointMutation()

  const mutationStatus = isEdit ? updateStatus : createStatus
  const { isLoading: isSubmitLoading } = mutationStatus

  const { data: allServiceProviders, isLoading: isAllServiceProvidersLoading } =
    useGetAllServiceProvidersQuery({
      sort_by: 'serviceProviderId',
      sort_order: SORT_ORDER_ASC,
      pta: salesPointDetails.pta ?? undefined
    })
  const serviceProviderOptions = (allServiceProviders?.resources ?? []).map(serviceProvider => {
    return {
      value: serviceProvider.service_provider_id,
      label: serviceProvider.name
    }
  })

  const [selectedReadClients, setSelectedReadClients] = useState<string[]>(
    salesPointDetails?.client_read ?? []
  )
  const [selectedWriteClients, setSelectedWriteClients] = useState<string[]>(
    salesPointDetails?.client_write ?? []
  )

  const handleChange = (
    value: string | string[] | number | boolean | undefined,
    field: keyof SalesPoint
  ) => {
    if (value !== undefined) {
      setSalesPointDetails(prevState => ({ ...prevState, [field]: value }) as SalesPoint)
    }
  }

  const handleDateTimePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = formatToUTCISO(value)
    }

    setSalesPointDetails(prevState => ({
      ...prevState,
      [name]: formattedValue
    }))
  }

  const handleAddressChange = (value: string, field: keyof SalesPointAddress) => {
    setSalesPointDetails(prevState => ({
      ...prevState,
      address: { ...prevState.address, [field]: value } as SalesPointAddress
    }))
  }

  const usageOptions = [
    {
      label: t`Production`,
      value: SalesPointUsage.PROD
    },
    {
      label: t`Test`,
      value: SalesPointUsage.TEST
    }
  ]

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const salesPointData = {
      ...salesPointDetails,
      client_read: selectedReadClients,
      client_write: selectedWriteClients
    }

    const mutation = isEdit
      ? updateSingleSalesPoint({
          ...salesPointData,
          sales_point_id: salesPoint.sales_point_id
        })
      : createSingleSalesPoint({
          ...salesPointData,
          pta: IS_HSL_SERVICE_ENVIRONMENT ? user?.pta : salesPointData.pta
        })

    mutation
      .unwrap()
      .then((salesPointResponse: SalesPoint) => {
        SnackbarUtils.success(t`Sales point successfully saved.`)
        if (selectedServiceProviderId) {
          navigate(
            appendQueryParamsToUrl(`${SERVICE_PROVIDER_MANAGEMENT_ROOT_URL}/service-providers`, {
              [SELECTED_SERVICE_PROVIDER_PARAMETER_KEY]: selectedServiceProviderId,
              [PTA_PARAM_KEY]: salesPointResponse.pta,
              [SERVICE_PROVIDER_DETAILS_DRAWER_TAB_PARAM_KEY]: ServiceProviderTabType.SALES_POINTS
            })
          )
        } else {
          navigate(
            appendQueryParamsToUrl(successBaseUrl, {
              [SELECTED_SALES_POINT_PARAMETER_KEY]: salesPointResponse.sales_point_id,
              [PTA_PARAM_KEY]: salesPointResponse.pta
            })
          )
        }
      })
      .catch(({ data: errorResponse }: { data: ServiceProviderManagementError }) => {
        const { violations } = errorResponse
        setValidationErrors(violations)
        SnackbarUtils.error(t`There was an error saving the sales point.`)
      })
  }

  return (
    <WithLoader loading={isAllServiceProvidersLoading}>
      <Grid container spacing={2} mb={4}>
        <Grid size={12}>
          <StyledForm onSubmit={handleSubmit} isDisabled={isSubmitLoading}>
            {!isEdit && IS_WALTTI_SERVICE_ENVIRONMENT && (
              <Box>
                <PTAFormField
                  value={salesPointDetails.pta}
                  onChange={value => handleChange(value, 'pta')}
                />
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormTextField
                name="name"
                required
                label={t`Name`}
                value={salesPointDetails?.name ?? ''}
                maxLength={40}
                onChange={e => handleChange(e.target.value, 'name')}
                {...getErrorProps('name')}
              />
              <FormTextField
                name="sales_point_id"
                required
                label={t`Sales point id`}
                value={salesPointDetails?.sales_point_id ?? ''}
                maxLength={9}
                onChange={e => handleChange(e.target.value, 'sales_point_id')}
                {...getErrorProps('sales_point_id')}
              />
              <FormTextField
                name="external_identifier"
                label={t`External identifier`}
                value={salesPointDetails?.external_identifier ?? ''}
                maxLength={50}
                onChange={e => handleChange(e.target.value, 'external_identifier')}
                {...getErrorProps('external_identifier')}
              />
            </Box>
            <Box>
              <FormControl
                sx={{
                  minWidth: 280
                }}
              >
                <Autocomplete
                  disablePortal
                  size="small"
                  options={serviceProviderOptions}
                  renderOption={(props, option) => (
                    <MenuItem {...props} key={option.value}>
                      {option.label}
                    </MenuItem>
                  )}
                  renderInput={params => (
                    <TextField required {...params} label={t`Service provider`} />
                  )}
                  onChange={(_, newValue) =>
                    handleChange(newValue?.value as string, 'service_provider_id')
                  }
                  value={
                    serviceProviderOptions.find(
                      option => option.value === salesPointDetails.service_provider_id
                    ) || null
                  }
                />
                <FormHelperText error={fieldErrors.service_provider_id.length > 0}>
                  {getFieldErrorHelperText('service_provider_id')}
                </FormHelperText>
              </FormControl>
            </Box>
            <Box>
              <StyledLabel>{t`Start date`}</StyledLabel>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CustomDateTimePicker
                onChange={handleDateTimePickerChange}
                name="start_date"
                readOnly={isEdit}
                value={
                  salesPointDetails?.start_date ? new Date(salesPointDetails?.start_date) : null
                }
                label={t`Start date`}
                isRequired={true}
                isError={fieldErrors.start_date?.length > 0}
                helperText={getFieldErrorHelperText('start_date')}
              />
            </Box>
            <Box>
              <StyledLabel>{t`Business information`}</StyledLabel>
            </Box>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormTextField
                name="business_id"
                label={t`Business id`}
                value={salesPointDetails?.business_id}
                maxLength={9}
                onChange={e => handleChange(e.target.value, 'business_id')}
                {...getErrorProps('business_id')}
              />
              {IS_HSL_SERVICE_ENVIRONMENT && (
                <>
                  <FormTextField
                    name="s365t_account_id"
                    label={t`S365 account id`}
                    value={salesPointDetails?.s365t_account_id}
                    onChange={e =>
                      handleChange(e.target.value.replaceAll(/\D/g, ''), 's365t_account_id')
                    }
                    slotProps={{ htmlInput: { inputMode: 'numeric' } }}
                    maxLength={30}
                    {...getErrorProps('s365t_account_id')}
                  />
                  <FormTextField
                    name="customer_reference_name"
                    label={t`Customer reference name`}
                    required
                    value={salesPointDetails?.customer_reference_name}
                    onChange={e => handleChange(e.target.value, 'customer_reference_name')}
                    {...getErrorProps('customer_reference_name')}
                  />
                </>
              )}
            </Box>
            <Box>
              <StyledLabel>{t`Address`}</StyledLabel>
            </Box>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <FormTextField
                name="street_address"
                label={t`Street address`}
                value={salesPointDetails?.address?.street_address ?? ''}
                onChange={e => handleAddressChange(e.target.value, 'street_address')}
                {...getErrorProps('street_address')}
              />
              <FormTextField
                name="postal_code"
                label={t`Postal code`}
                value={salesPointDetails?.address?.postal_code ?? ''}
                onChange={e => handleAddressChange(e.target.value, 'postal_code')}
                {...getErrorProps('postal_code')}
              />
              <FormTextField
                name="city"
                label={t`City`}
                value={salesPointDetails?.address?.city ?? ''}
                onChange={e => handleAddressChange(e.target.value, 'city')}
                {...getErrorProps('city')}
              />
            </Box>
            <Box>
              <FormTextField
                name="phone"
                label={t`Phone`}
                value={salesPointDetails?.phone}
                onChange={e => handleChange(e.target.value, 'phone')}
                {...getErrorProps('phone')}
              />
            </Box>
            <Box>
              <StyledLabel>{t`Usage`}</StyledLabel>
            </Box>
            <Box>
              <FormControl sx={{ minWidth: FORM_FIELD_DEFAULT_MIN_WIDTH }} required>
                <InputLabel size="small">{t`Usage`}</InputLabel>
                <Select
                  name="usage"
                  required
                  size="small"
                  label={t`Usage`}
                  value={salesPointDetails.usage ?? ''}
                  error={fieldErrors.usage.length > 0}
                  onChange={e => handleChange(e.target.value, 'usage')}
                >
                  {usageOptions.map(({ label, value }) => (
                    <MenuItem key={value} value={value}>
                      {label}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText error={fieldErrors.usage.length > 0}>
                  {getFieldErrorHelperText('usage')}
                </FormHelperText>
              </FormControl>
            </Box>
            <Box>
              <StyledLabel>{t`Sales`}</StyledLabel>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={salesPointDetails.sales_allowed}
                    onChange={e => handleChange(e.target.checked, 'sales_allowed')}
                    name="sales_allowed"
                    color="primary"
                  />
                }
                label={t`Sales allowed`}
              />
              <FormHelperText error={fieldErrors.sales_allowed.length > 0}>
                {getFieldErrorHelperText('sales_allowed')}
              </FormHelperText>
            </Box>
            {IS_HSL_SERVICE_ENVIRONMENT && (
              <>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={salesPointDetails.hsl_sales_point}
                        onChange={e => handleChange(e.target.checked, 'hsl_sales_point')}
                        name="hsl_sales_point"
                        color="primary"
                      />
                    }
                    label={t`HSL sales point`}
                  />
                  <FormHelperText error={fieldErrors.hsl_sales_point.length > 0}>
                    {getFieldErrorHelperText('hsl_sales_point')}
                  </FormHelperText>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={salesPointDetails.billed_at_sales_point_level}
                        onChange={e =>
                          handleChange(e.target.checked, 'billed_at_sales_point_level')
                        }
                        name="billed_at_sales_point_level"
                        color="primary"
                        disabled={!salesPointDetails.s365t_account_id}
                      />
                    }
                    label={t`Billed at sales point level`}
                  />
                  {!isEdit && (
                    <InfoTooltip
                      title={
                        <Typography variant="body2">
                          {t`Copied from the service provider to the new sales point.`}
                        </Typography>
                      }
                    />
                  )}

                  <FormHelperText error={fieldErrors.billed_at_sales_point_level.length > 0}>
                    {getFieldErrorHelperText('billed_at_sales_point_level')}
                  </FormHelperText>
                </Box>
                <Box>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={salesPointDetails.transfer_sales_to_financial_system}
                        onChange={e =>
                          handleChange(e.target.checked, 'transfer_sales_to_financial_system')
                        }
                        name="transfer_sales_to_financial_system"
                        color="primary"
                        disabled={!salesPointDetails.s365t_account_id}
                      />
                    }
                    label={t`Transfer sales to financial system`}
                  />
                  <FormHelperText error={fieldErrors.transfer_sales_to_financial_system.length > 0}>
                    {getFieldErrorHelperText('transfer_sales_to_financial_system')}
                  </FormHelperText>
                </Box>
              </>
            )}
            <Box>
              <StyledLabel>{t`Source`}</StyledLabel>
              <FormTextField
                name="source"
                label={t`Source`}
                value={salesPointDetails?.source}
                onChange={e => handleChange(e.target.value, 'source')}
                maxLength={256}
                {...getErrorProps('source')}
              />
            </Box>
            <Box>
              <StyledLabel>{t`Additional information`}</StyledLabel>
            </Box>
            <Box sx={{ display: 'flex' }}>
              <FormTextField
                sx={{ minWidth: 600 }}
                name="additional_info"
                label={t`Additional information`}
                value={salesPointDetails?.additional_info}
                onChange={e => handleChange(e.target.value, 'additional_info')}
                multiline
                rows={6}
                {...getErrorProps('additional_info')}
              />
            </Box>
            <Box sx={{ maxWidth: ALLOWED_API_CLIENTS_CONTAINER_MAX_WIDTH, py: 2 }}>
              <AllowedApiClients
                service={Service.SERVICE_PROVIDER_MANAGEMENT}
                selectedReadClients={selectedReadClients}
                selectedWriteClients={selectedWriteClients}
                setSelectedReadClients={setSelectedReadClients}
                setSelectedWriteClients={setSelectedWriteClients}
              />
            </Box>
            <CustomLoadingButton
              isLoading={isSubmitLoading}
              type="submit"
              size="large"
              iconPosition="end"
            >
              {t`Save sales point`}
            </CustomLoadingButton>
          </StyledForm>
        </Grid>
      </Grid>
    </WithLoader>
  )
}

export default SalesPointForm
